import { inject } from 'inversify';
import i18n from '../../config/i18n';
import { APP_TIMEZONE } from '../../config/timezone';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import {
  type CreateTemperatureCheckRequest,
  type EquipmentStatusList,
  type EquipmentType,
  HACCP_TEMPERATURE_RANGES,
  type SignEquipmentBatchRequest,
  type SignEquipmentBatchResult,
  type TemperatureCalendarResponse,
  type TemperatureCheck,
  type TemperatureCheckStatus,
  type TemperatureValidationResult,
} from './temperature-check.types';

// Interfaccia per il record API
interface EquipmentCheckRecordApi {
  id: string;
  tenantId: string;
  equipmentId: string;
  equipmentName: string;
  equipmentType: string;
  performedBy: string | null;
  performerName: string;
  signatureStorageKey: string | null;
  data: {
    temperature?: number;
    isCompliant?: boolean;
    message?: string;
    validatedAt?: string;
    [key: string]: unknown;
  };
  createdAt: string;
  updatedAt: string;
  pendingSignature: boolean;
  ingestedVia: string | null;
}

export interface TemperatureRangeOverride {
  min?: number | null;
  max?: number | null;
}

export interface CorrectTemperatureCheckRequest {
  temperature: number;
  reason?: string;
}

/**
 * What an operator sends to confirm or dismiss an IoT-written suggestion
 * (real-time breach, cumulative-risk, or the nightly rollup's out-of-range
 * day): the temperature they measured by hand and the outcome they declare.
 * Mirrors `ReviewIotSuggestionDto` in `shared/dto/temperature`.
 */
export interface ReviewIotSuggestionRequest {
  temperature: number;
  isCompliant: boolean;
  signatureImage: string;
}

export interface ITemperatureCheckService {
  validateTemperature(
    temperature: number,
    equipmentType: EquipmentType,
    rangeOverride?: TemperatureRangeOverride,
  ): TemperatureValidationResult;
  create(data: CreateTemperatureCheckRequest): Promise<TemperatureCheck>;
  correctRecord(recordId: string, data: CorrectTemperatureCheckRequest): Promise<TemperatureCheck>;
  findAll(from?: string, to?: string, equipmentId?: string): Promise<TemperatureCheck[]>;
  getCalendar(from: string, to: string): Promise<TemperatureCalendarResponse>;
  signBatch(request: SignEquipmentBatchRequest): Promise<SignEquipmentBatchResult>;
  getTodayStatus(): Promise<TemperatureCheckStatus>;
  getEquipmentStatus(): Promise<EquipmentStatusList>;
  /** Confirms or dismisses an open IoT suggestion — always produces a normal
   * signed human record; the suggestion itself is only annotated. */
  reviewIotSuggestion(
    suggestionRecordId: string,
    data: ReviewIotSuggestionRequest,
  ): Promise<TemperatureCheck>;
}

@Injectable()
class TemperatureCheckService implements ITemperatureCheckService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  validateTemperature(
    temperature: number,
    equipmentType: EquipmentType,
    rangeOverride?: TemperatureRangeOverride,
  ): TemperatureValidationResult {
    const baseRange = HACCP_TEMPERATURE_RANGES[equipmentType];
    const range = {
      ...baseRange,
      min: rangeOverride?.min ?? baseRange.min,
      max: rangeOverride?.max ?? baseRange.max,
    };

    let isCompliant: boolean;
    let message: string;

    const formattedTemp = temperature.toFixed(2);

    // Dispatch on the shape of the range, not on the equipment type: an absent
    // bound means that side is unconstrained. A freezer (max-only) and a
    // hot-holding cabinet (min-only) are duals of the same rule. Mirrors
    // backend/src/haccp/haccp-validator.service.ts validateTemperature().
    if (range.min === null && range.max !== null) {
      isCompliant = temperature <= range.max;
      message = `${formattedTemp}°C (≤ ${range.max.toFixed(2)}°C)`;
    } else if (range.max === null && range.min !== null) {
      isCompliant = temperature >= range.min;
      message = `${formattedTemp}°C (≥ ${range.min.toFixed(2)}°C)`;
    } else if (range.min !== null && range.max !== null) {
      isCompliant = temperature >= range.min && temperature <= range.max;
      message = `${formattedTemp}°C (${range.min.toFixed(2)}°C - ${range.max.toFixed(2)}°C)`;
    } else {
      // Both bounds absent: nothing to check against. Fail closed rather than
      // silently declaring a HACCP record compliant.
      isCompliant = false;
      message = `${formattedTemp}°C`;
    }

    return {
      isCompliant,
      temperature,
      equipmentType,
      range,
      message,
    };
  }

  private mapToTemperatureCheck(record: EquipmentCheckRecordApi): TemperatureCheck {
    return {
      id: record.id,
      equipmentId: record.equipmentId,
      equipmentName: record.equipmentName,
      equipmentType: record.equipmentType as EquipmentType,
      temperature: record.data.temperature ?? 0,
      isCompliant: record.data.isCompliant ?? false,
      recordedAt: record.createdAt,
      performedBy: record.performedBy ?? undefined,
      performerName: record.performerName ?? undefined,
      signatureImage:
        typeof record.data.signatureImage === 'string' ? record.data.signatureImage : undefined,
      pendingSignature: record.pendingSignature,
      ingestedVia: record.ingestedVia,
    };
  }

  async create(data: CreateTemperatureCheckRequest): Promise<TemperatureCheck> {
    const response = await this.httpService.post<EquipmentCheckRecordApi[]>(
      'equipment-check-records',
      {
        records: [
          {
            equipmentId: data.equipmentId,
            equipmentName: data.equipmentName,
            equipmentType: data.equipmentType,
            data: { temperature: data.temperature },
          },
        ],
      },
    );

    return this.mapToTemperatureCheck(response[0]);
  }

  async correctRecord(
    recordId: string,
    data: CorrectTemperatureCheckRequest,
  ): Promise<TemperatureCheck> {
    const response = await this.httpService.post<EquipmentCheckRecordApi>(
      `equipment-check-records/${recordId}/corrections`,
      data,
    );
    return this.mapToTemperatureCheck(response);
  }

  async findAll(from?: string, to?: string, equipmentId?: string): Promise<TemperatureCheck[]> {
    const params = new URLSearchParams();
    if (from) {
      params.set('from', from);
    }
    if (to) {
      params.set('to', to);
    }
    if (equipmentId) {
      params.set('equipmentId', equipmentId);
    }
    params.set('tz', APP_TIMEZONE);

    const queryString = params.toString();
    const url = `equipment-check-records?${queryString}`;

    const records = await this.httpService.get<EquipmentCheckRecordApi[]>(url);

    return records
      .filter((r) => r.data.temperature !== undefined)
      .map((r) => this.mapToTemperatureCheck(r));
  }

  async getCalendar(from: string, to: string): Promise<TemperatureCalendarResponse> {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    params.set('tz', APP_TIMEZONE);

    const url = `equipment-check-records/temperature-calendar?${params.toString()}`;
    const response = await this.httpService.get<TemperatureCalendarResponse>(url);

    if (!response?.calendar || !response.stats) {
      throw new Error(i18n.t('temperature.errors.invalidApiResponse'));
    }

    return response;
  }

  async signBatch(request: SignEquipmentBatchRequest): Promise<SignEquipmentBatchResult> {
    const tz = APP_TIMEZONE;
    const response = await this.httpService.post<{
      records: EquipmentCheckRecordApi[];
      skippedEquipment: string[];
    }>(`equipment-check-records/sign-batch?tz=${encodeURIComponent(tz)}`, request);
    return {
      records: response.records.map((r) => this.mapToTemperatureCheck(r)),
      skippedEquipment: response.skippedEquipment ?? [],
    };
  }

  async getTodayStatus(): Promise<TemperatureCheckStatus> {
    const tz = APP_TIMEZONE;
    return this.httpService.get<TemperatureCheckStatus>(
      `equipment-check-records/today-status?tz=${encodeURIComponent(tz)}`,
    );
  }

  async getEquipmentStatus(): Promise<EquipmentStatusList> {
    const tz = APP_TIMEZONE;
    return this.httpService.get<EquipmentStatusList>(
      `equipment-check-records/equipment-status?tz=${encodeURIComponent(tz)}`,
    );
  }

  async reviewIotSuggestion(
    suggestionRecordId: string,
    data: ReviewIotSuggestionRequest,
  ): Promise<TemperatureCheck> {
    const response = await this.httpService.post<EquipmentCheckRecordApi>(
      `equipment-check-records/${suggestionRecordId}/review`,
      data,
    );
    return this.mapToTemperatureCheck(response);
  }
}

export { TemperatureCheckService };
